/**
 * The account menu: who is signed in, where their settings are, which theme
 * the app uses, and the way out.
 *
 * These three belong together — they are all "about me", not about the page —
 * and putting them behind the name keeps the header to one control instead of
 * three competing ones.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, LogOut, UserCircle, Sun, Moon, Monitor, Check } from 'lucide-react';
import { useAuthStore } from '@/store/auth.store';
import { useAuth } from '@/hooks/useAuth';
import { useThemeStore, type ThemeMode } from '@/store/theme.store';
import clsx from 'clsx';
import { humanize } from '@/utils/labels';

const THEMES: { mode: ThemeMode; icon: typeof Sun; label: string }[] = [
  { mode: 'light',  icon: Sun,     label: 'Light' },
  { mode: 'dark',   icon: Moon,    label: 'Dark' },
  { mode: 'system', icon: Monitor, label: 'System' },
];

export default function ProfileMenu({ onDark = false }: { onDark?: boolean }) {
  const user = useAuthStore(s => s.user);
  const { logout } = useAuth();
  const { mode, setMode } = useThemeStore();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  // Close on a click anywhere else, and on Escape — a menu that traps the
  // page is worse than no menu.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const initials = user
    ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase()
    : '?';

  // The header band is light in the light theme and dark in the dark one, so
  // the trigger has to read against either.
  const trigger = onDark
    ? 'bg-white/15 hover:bg-white/25 text-white'
    : 'bg-gray-100 hover:bg-gray-200 text-gray-800';

  return (
    <div className="relative" ref={wrap}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={clsx('flex items-center gap-2 rounded-lg px-2 py-1.5 transition sm:px-3', trigger)}
      >
        <span
          className={clsx(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
            'bg-dl-orange text-dl-orange-on',
          )}
        >
          {initials}
        </span>
        <span className="hidden max-w-[140px] truncate text-sm font-medium sm:inline">
          {user ? `${user.firstName} ${user.lastName}` : 'User'}
        </span>
        <span className={clsx(
          'hidden rounded px-2 py-0.5 ap-label md:inline',
          onDark ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-700',
        )}>
          {humanize(user?.role)}
        </span>
        <ChevronDown className={clsx('hidden h-3.5 w-3.5 transition-transform sm:inline', open && 'rotate-180')} />
      </button>

      {open && (
        <div
          role="menu"
          className="chrome-reset absolute right-0 z-50 mt-2 w-60 overflow-hidden rounded-xl border border-gray-200 bg-surface text-gray-900 shadow-xl"
        >
          <div className="border-b border-gray-200 px-4 py-3">
            <p className="truncate text-sm font-semibold text-gray-900">
              {user ? `${user.firstName} ${user.lastName}` : 'User'}
            </p>
            <p className="truncate text-xs text-gray-500">{user?.email}</p>
          </div>

          <Link
            to="/profile"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            <UserCircle className="h-4 w-4 text-gray-500" />
            Profile settings
          </Link>

          <div className="border-t border-gray-200 px-4 pb-2 pt-3">
            <p className="mb-1.5 ap-label text-gray-500">Theme</p>
            <div className="space-y-0.5">
              {THEMES.map(({ mode: m, icon: Icon, label }) => (
                <button
                  key={m}
                  type="button"
                  role="menuitemradio"
                  aria-checked={mode === m}
                  onClick={() => setMode(m)}
                  className={clsx(
                    'flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm transition-colors',
                    mode === m ? 'bg-gray-100 font-medium text-gray-900' : 'text-gray-600 hover:bg-gray-50',
                  )}
                >
                  <Icon className={clsx('h-4 w-4', mode === m ? 'text-amber-500' : 'text-gray-400')} />
                  <span className="flex-1 text-left">{label}</span>
                  {mode === m && <Check className="h-3.5 w-3.5 text-gray-900" />}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); logout(); }}
            className="flex w-full items-center gap-2.5 border-t border-gray-200 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
